import React from 'react';
import styled, { keyframes } from 'styled-components';

const divineGlow = keyframes`
  0%, 100% { opacity: 0.3; filter: blur(15px); }
  50% { opacity: 0.5; filter: blur(18px); }
`;

const breathe = keyframes`
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
  50% { transform: translateY(-4px) scale(1.02); opacity: 0.6; }
`;

const heartPulse = keyframes`
  0%, 100% { transform: scale(1); }
  10% { transform: scale(1.06); }
  20% { transform: scale(1); }
  30% { transform: scale(1.04); }
  40% { transform: scale(1); }
`;

const flicker = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
`;

const drift = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(2px); }
`;

const stateFloat = keyframes`
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.18; }
`;

const Container = styled.div`
  position: relative;
  width: 340px;
  height: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
`;

/* Spirit - Divine */
const SpiritZone = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DivineLight = styled.div`
  position: absolute;
  top: -10px;
  width: 200px;
  height: 60px;
  background: radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, transparent 70%);
  animation: ${divineGlow} 8s ease-in-out infinite;
`;

const SpiritForm = styled.div`
  width: 140px;
  height: 25px;
  background: radial-gradient(ellipse at center, rgba(255,255,255,0.18) 0%, transparent 70%);
  filter: blur(6px);
  animation: ${breathe} 10s ease-in-out infinite;
`;

const Label = styled.div`
  margin-top: 10px;
  font-size: 11px;
  color: rgba(255,255,255,${props => props.opacity || 0.4});
  letter-spacing: 0.25em;
  font-family: 'Space Grotesk', sans-serif;
  text-transform: lowercase;
`;

const Desc = styled.div`
  margin-top: 4px;
  font-size: 8px;
  color: rgba(255,255,255,0.18);
  letter-spacing: 0.12em;
  font-family: 'Inter', sans-serif;
`;

/* Connection lines */
const PullLine = styled.div`
  width: 1px;
  height: 50px;
  background: linear-gradient(${props => props.direction || 'to top'}, rgba(255,255,255,0.25), transparent);
`;

/* Heart - Center */
const HeartZone = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeartOuter = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${heartPulse} 3s ease-in-out infinite;
`;

const IntellectRing = styled.div`
  position: absolute;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px dashed rgba(255,255,255,0.12);
  animation: ${flicker} 5s ease-in-out infinite;
`;

const HeartCore = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.5) 50%, transparent 70%);
  box-shadow: 0 0 40px rgba(255,255,255,0.25);
`;

const SideLabel = styled.div`
  position: absolute;
  font-size: 7px;
  color: rgba(255,255,255,0.2);
  letter-spacing: 0.08em;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
`;

const StateList = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const State = styled.div`
  font-size: 7px;
  color: rgba(255,255,255,0.1);
  letter-spacing: 0.06em;
  font-family: 'Inter', sans-serif;
  animation: ${stateFloat} ${props => props.dur || 8}s ease-in-out infinite;
`;

/* Self - Lower */
const SelfZone = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SelfLine = styled.div`
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  border-radius: 1px;
  animation: ${drift} 6s ease-in-out infinite;
`;

/* World - Bottom */
const WorldLine = styled.div`
  width: 140px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
`;

export const LogoArt = () => {
  return (
    <Container>
      {/* Spirit */}
      <SpiritZone>
        <DivineLight />
        <SpiritForm />
        <Label opacity={0.45}>spirit</Label>
        <Desc>divine essence</Desc>
      </SpiritZone>

      <PullLine direction="to top" />

      {/* Heart */}
      <HeartZone>
        <StateList style={{ left: -90, top: '50%', transform: 'translateY(-50%)' }}>
          <State dur={10}>blind</State>
          <State dur={9}>afflicted</State>
          <State dur={8}>returning</State>
          <State dur={7}>sound</State>
          <State dur={6}>tranquil</State>
        </StateList>

        <HeartOuter>
          <IntellectRing />
          <HeartCore />
        </HeartOuter>

        <SideLabel style={{ right: -70, top: '50%', transform: 'translateY(-50%)' }}>
          intellect
        </SideLabel>

        <Label opacity={0.55}>heart</Label>
        <Desc>seat of consciousness</Desc>
      </HeartZone>

      <PullLine direction="to bottom" />

      {/* Self */}
      <SelfZone>
        <StateList style={{ right: -80, top: 0 }}>
          <State dur={6}>pure</State>
          <State dur={6.5}>content</State>
          <State dur={7}>inspired</State>
          <State dur={7.5}>conscience</State>
          <State dur={8}>desire</State>
        </StateList>

        <SelfLine />
        <Label opacity={0.4}>self</Label>
        <Desc>ego and desire</Desc>
      </SelfZone>

      {/* World */}
      <WorldLine />
      <Desc style={{ marginTop: 8, opacity: 0.12 }}>world</Desc>
    </Container>
  );
};
